import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, CheckCircle, AlertTriangle, HelpCircle, Mail, X } from 'lucide-react';
import './ContactSection.css';

// Brevo API Configuration
const BREVO_API_KEY = import.meta.env.VITE_BREVO_API_KEY;
const BREVO_LIST_IDS = [3]; // Using List ID 3 as provided
const BREVO_DOI_TEMPLATE_ID = 8; // Double opt-in confirmation template
const REDIRECT_URL = window.location.origin + '/thank-you'; // Change this to your desired redirect page

function ContactSection() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    VORNAME: '',
    NACHNAME: '',
    EMAIL: '',
    INTERESSE: ''
  });
  const [status, setStatus] = useState('idle'); // idle, sending, success, error
  const [statusMessage, setStatusMessage] = useState('');
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);

  // Check for confirmation parameter in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('confirmed') === 'true') {
      setStatus('success');
      setStatusMessage(t('contactStatusConfirmed') || 'Thank you! Your email has been confirmed.');
      
      // Remove the confirmation parameter from URL without refreshing
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({ ...prevState, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setStatusMessage('');

    if (!formData.EMAIL) {
      setStatus('error');
      setStatusMessage(t('contactFormErrorEmailRequired') || 'Email is required.'); // Fallback text
      return;
    }

    const payload = {
      email: formData.EMAIL,
      attributes: {
        FIRSTNAME: formData.VORNAME,
        LASTNAME: formData.NACHNAME,
        INTERESSE: formData.INTERESSE
      },
      includeListIds: BREVO_LIST_IDS,
      updateEnabled: true,
      redirectionUrl: REDIRECT_URL,
      templateId: BREVO_DOI_TEMPLATE_ID
    };

    try {
      console.log('Sending payload to Brevo:', JSON.stringify(payload, null, 2));
      
      const response = await fetch('https://api.brevo.com/v3/contacts/doubleOptinConfirmation', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'api-key': BREVO_API_KEY
        },
        body: JSON.stringify(payload)
      });

      const responseData = await response.json();
      console.log('Brevo API Response:', JSON.stringify({
        status: response.status,
        statusText: response.statusText,
        data: responseData,
        headers: Object.fromEntries(response.headers.entries())
      }, null, 2));

      if (response.ok) {
        console.log('Contact created/updated successfully:', JSON.stringify(responseData, null, 2));
        console.log('Confirmation email should be sent with template ID:', payload.templateId);
        console.log('Redirection URL set to:', payload.redirectionUrl);
        
        setStatus('success');
        setStatusMessage(t('contactStatusSuccess') || 'Thank you! Your message has been sent.'); // Fallback text
        setShowSuccessOverlay(true);
      } else {
        console.error('Brevo API Error:', JSON.stringify({
          status: response.status,
          statusText: response.statusText,
          data: responseData
        }, null, 2));
        
        // Handle specific error cases
        if (responseData.code === 'missing_parameter' && responseData.message.includes('template')) {
          setStatus('error');
          setStatusMessage('Double opt-in template is not configured. Please contact support.');
        } else {
          setStatus('error');
          setStatusMessage(responseData.message || t('contactStatusError') || 'Something went wrong. Please try again.');
        }
      }
    } catch (error) {
      console.error('Network or other error:', error);
      setStatus('error');
      setStatusMessage(t('contactStatusErrorNetwork') || 'A network error occurred. Please try again.');
    }
  };

  const SuccessOverlay = () => (
    <div className="success-overlay">
      <div className="success-overlay-content">
        <button 
          className="close-overlay-button"
          onClick={() => setShowSuccessOverlay(false)}
          aria-label="Close overlay"
        >
          <X size={24} />
        </button>
        <div className="success-icon">
          <Mail size={48} />
        </div>
        <h3 className="success-title">{t('contactStatusSuccess')}</h3>
        <p className="success-text">
          {t('confirmationEmailSent', 'We have sent a confirmation email to')} <span className="email-highlight">{formData.EMAIL}</span>
        </p>
        <p className="success-subtext">
          {t('confirmationEmailInstructions', 'Please check your inbox and click the confirmation link to complete your registration.')}
        </p>
      </div>
    </div>
  );

  return (
    <section id="contact" className="contact-section content-panel">
      <h2 className="section-title">{t('contactTitle')}</h2>
      <p className="section-subtitle">{t('contactSubtitle')}</p>

      <div className="contact-layout-grid">
        <div className="contact-form-area">
          <form onSubmit={handleSubmit} className="contact-form">
            <div className="form-group">
              <input
                type="text"
                id="VORNAME"
                name="VORNAME"
                value={formData.VORNAME}
                onChange={handleChange}
                placeholder={t('contactFormPlaceholderVorname') || 'Enter your first name'}
              />
            </div>

            <div className="form-group">
              <input
                type="text"
                id="NACHNAME"
                name="NACHNAME"
                value={formData.NACHNAME}
                onChange={handleChange}
                placeholder={t('contactFormPlaceholderNachname') || 'Enter your last name'}
              />
            </div>

            <div className="form-group">
              <input
                type="email"
                id="EMAIL"
                name="EMAIL"
                value={formData.EMAIL}
                onChange={handleChange}
                required
                placeholder={t('contactFormPlaceholderEmail') || 'Enter your email address'}
              />
            </div>

            <div className="form-group">
              <textarea
                id="INTERESSE"
                name="INTERESSE"
                value={formData.INTERESSE}
                onChange={handleChange}
                rows="4"
                placeholder={t('contactFormPlaceholderInteresse') || 'Let us know your investment interest or any questions you have.'}
              />
            </div>
            
            <button type="submit" className={`cta-button ${status === 'sending' ? 'sending' : ''}`} disabled={status === 'sending'}>
              {status === 'sending' ? (
                <Loader2 className="icon animate-spin" size={20} />
              ) : (
                t('contactFormSubmitButton')
              )}
            </button>

            {status === 'error' && (
              <div className="form-status-message error-message">
                <AlertTriangle size={20} /> <p>{statusMessage}</p>
              </div>
            )}
            {/* New Disclaimers Start */}
            <div className="form-disclaimer-text">
              <p>{t('contactDisclaimer1') || 'Please only register here if you plan to invest. The tickets for the private round are limited.'}</p>
            </div>
            <div className="form-disclaimer-text">
              <p>{t('contactDisclaimer2') || 'We wont send you SPAM. Just info about the status of the fundraising.'}</p>
            </div>
            {/* New Disclaimers End */}
            <div className="form-disclaimer-text">
              <p>
                {t('brevoPrivacyPolicyTextP1') || 'We use Brevo as our marketing platform. By submitting this form, you acknowledge that the information you provide will be transferred to Brevo for processing in accordance with their'} <a href="https://www.brevo.com/legal/privacypolicy/" target="_blank" rel="noopener noreferrer" style={{color: 'var(--primary)'}}>{t('brevoPrivacyPolicyLinkText') || 'privacy policy'}</a>.
              </p>
            </div>
          </form>
        </div>

        <div className="contact-info-area">
          <div className="info-box why-invest">
            <h3 className="box-title">{t('investorOfferWhyTitle')}</h3>
            <ul className="box-list">
              <li><CheckCircle className="list-icon" size={16} /> {t('investorOfferWhyItem1')}</li>
              <li><CheckCircle className="list-icon" size={16} /> {t('investorOfferWhyItem2')}</li>
              <li><CheckCircle className="list-icon" size={16} /> {t('investorOfferWhyItem3')}</li>
              <li><CheckCircle className="list-icon" size={16} /> {t('investorOfferWhyItem4')}</li>
            </ul>
          </div>
          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                const faqButton = document.querySelector('.floating-faq-button');
                if (faqButton) faqButton.click();
              }}
              style={{ 
                color: 'var(--color-primary)',
                textDecoration: 'none',
                fontSize: '0.95rem',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <HelpCircle size={16} />
              {t('faqButtonText')}
            </a>
          </div>
        </div>
      </div>

      {showSuccessOverlay && <SuccessOverlay />}
    </section>
  );
}

export default ContactSection; 